import { type FC } from 'react';

import Pagination from 'molecules/Pagination';
import Section from 'molecules/Section';
import { splitSectionProps } from 'molecules/Section/config';

import ScrollFadeSection from 'components/Animations/ScrollFadeSection';

import StandardFeed from 'layout/Listing/components/StandardFeed';
import { ListingConfigProps } from 'layout/Listing/config';
import usePagination from 'layout/Listing/hooks/usePagination';
import useSaveReturnUrl from 'layout/Listing/hooks/useSaveReturnUrl';
import SearchBar from 'layout/Listing/variants/customerStories/components/SearchBar';
import useCustomerStoryFilterSearchParam from 'layout/Listing/variants/customerStories/hooks/useCustomerStoryFilterSearchParam';
import useFilterCustomerStory from 'layout/Listing/variants/customerStories/hooks/useFilteredCustomerStory';

import { hasArrayValues } from 'utils/array/arrays';
import { mergeListings } from 'utils/array/mergeListings';
import { toArray } from 'utils/array/toArray';
import { getBuilderStateData } from 'utils/builder';
import useForceReloadOnBackToResources from 'utils/hooks/useForceReloadOnBackToResources';

import stories from 'data/customerStories.json';

const PAGE_SIZE = 12;

type StoryItem = { data: CustomerStoryResource };

const CustomerStoryListing: FC<ListingConfigProps> = props => {
  const {
    sectionProps,
    rest: { newsletterCard, builderState, searchPlaceholder, headingTitle },
  } = splitSectionProps(props);

  const rawLatestData = getBuilderStateData('listingData', builderState);
  const latestData = toArray<StoryItem>(rawLatestData);

  const localData = toArray<StoryItem>(stories);

  const storiesList: StoryItem[] = hasArrayValues(latestData) ? mergeListings(latestData, localData) : localData;

  if (!hasArrayValues(storiesList)) {
    console.warn('[CustomerStoryListing] No items to render – check builderState/listingData and customerStories.json');
  }

  useCustomerStoryFilterSearchParam();

  const filteredStories = useFilterCustomerStory(storiesList).sort((a, b) => {
    const aDate = a.data?.priority ? 1 : 0;
    const bDate = b.data?.priority ? 1 : 0;

    return bDate - aDate;
  });

  const { currentPage, paginatedItems, totalItems, handlePageChange } = usePagination({
    items: filteredStories,
    pageSize: PAGE_SIZE,
  });

  useSaveReturnUrl('/customers');

  useForceReloadOnBackToResources();

  return (
    <Section {...sectionProps} darkMode={false}>
      <ScrollFadeSection delay={0.1} containPaint={false}>
        <div id="listing-section" className="flex flex-col gap-8">
          <div className="flex flex-col justify-between gap-4 lg:flex-row lg:items-center">
            <h2 className="font-spaceGrotesk text-display-md font-semibold text-gray-900 sm:max-lg:col-span-full">
              {headingTitle || 'All Customer Stories'}
            </h2>

            <SearchBar placeholder={searchPlaceholder} />
          </div>

          <StandardFeed posts={paginatedItems} newsletterCard={newsletterCard} />

          {totalItems > PAGE_SIZE && (
            <Pagination
              count={totalItems}
              pageSize={PAGE_SIZE}
              setPage={detail => handlePageChange(detail.page)}
              currentPage={currentPage}
            />
          )}
        </div>
      </ScrollFadeSection>
    </Section>
  );
};

export default CustomerStoryListing;
